/**
 * Shared formatting for a patient's `motivations` / `diet_restrictions` tags so
 * the two display surfaces — the rail (sidebar) and the header strip — always
 * read identically. Both fields arrive as `PatientNamedTag[]` (name + optional
 * free-text comment).
 */

export type NamedTag = { name: string; comment?: string | null };

/**
 * Mirrors v1's `joinPatientMotivationsRestrictions`: skip "other" rows that
 * carry no comment, and substitute the `comment` text when `name === "other"`.
 */
export const formatPatientTags = (tags: NamedTag[] | undefined): string[] => {
  if (!tags || tags.length === 0) return [];
  return tags
    .filter((tag) => tag.name !== 'other' || !!tag.comment)
    .map((tag) => (tag.name === 'other' ? tag.comment ?? '' : tag.name))
    .filter(Boolean);
};

/**
 * `diet_restrictions` carries the patient app's canonical "no restrictions"
 * answer as a literal `everything` tag ("I eat everything" on intake). Strip it
 * so a clinician doesn't read the field as a restriction called "everything".
 */
export const formatDietRestrictions = (tags: NamedTag[] | undefined): string[] =>
  formatPatientTags(tags).filter((r) => r.toLowerCase() !== 'everything');

/** Motivations as a comma-joined string, or an em-dash when none are on file. */
export const motivationsDisplay = (tags: NamedTag[] | undefined): string => {
  const list = formatPatientTags(tags);
  return list.length ? list.join(', ') : '—';
};

/**
 * Dietary restrictions as a display string: em-dash for "no data on file",
 * `noneLabel` (e.g. localized "None") when the only entry was the "everything"
 * sentinel, otherwise a comma-joined list.
 */
export const dietRestrictionsDisplay = (
  tags: NamedTag[] | undefined,
  noneLabel: string,
): string => {
  if (!tags || formatPatientTags(tags).length === 0) return '—';
  const list = formatDietRestrictions(tags);
  return list.length === 0 ? noneLabel : list.join(', ');
};
